import { useState, useMemo } from "react";
import {
    UsersRound, Plus, Pencil, Trash2, Check,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useSizing } from "@/lib/sizing-store";
import { PageHeader, EmptyState, PrimaryButton, DeleteConfirmDialog } from "@/components/design-system";

const initials = (name) =>
    name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

/**
 * YAPILANDIRMA > MODELLER
 *
 * Model kütüphanesi. Model→Ürün eşleştirme UI KALDIRILDI (2026-07-27):
 * artık atama YALNIZCA ürün tarafından (ürün → model) yapılır. Bu sayfa
 * sadece model tanımlaması + boy/kilo/ölçüler kütüphanesidir.
 */
export default function Models() {
    const { models, products, removeModel } = useSizing();
    const [formModel, setFormModel] = useState(null); // null | "new" | model object
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Kaç ürün bu modeli giyiyor? (silme onayında kullanıcıya bilgi vermek için)
    // Product-tarafı FK okuyoruz — cascade yönünü şeffaflaştırıyoruz.
    const usageByModelId = useMemo(() => {
        const map = {};
        for (const p of products) {
            if (p.modelId) map[p.modelId] = (map[p.modelId] || 0) + 1;
        }
        return map;
    }, [products]);

    return (
        <div className="space-y-6" data-testid="models-page">
            <PageHeader
                icon={UsersRound}
                title="Modeller"
                description={<>Ürünlerini giyen modellerin boy, kilo ve vücut ölçüleri kütüphanesi. Bir modele hangi ürünün bağlı olduğunu <span className="font-medium text-foreground">Ürünler</span> sayfasından atarsın.</>}
                actions={
                    <PrimaryButton
                        onClick={() => setFormModel("new")}
                        data-testid="model-add-btn"
                    >
                        <Plus className="h-3.5 w-3.5" /> Model Ekle
                    </PrimaryButton>
                }
            />

            {/* Cards */}
            {models.length === 0 ? (
                <EmptyState
                    icon={UsersRound}
                    title="Henüz model yok"
                    description={"Modeller, ürünlerinin nasıl göründüğünü müşterine gösterir — boy, kilo ve ölçülerle tanımlanır. Bir kere ekle; sonra Ürünler sayfasından \u201cbu üründe M beden giyer\u201d şeklinde bağla."}
                    action={{
                        label: "İlk Modeli Ekle",
                        onClick: () => setFormModel("new"),
                        icon: Plus,
                        testId: "empty-create-model",
                    }}
                    testId="models-empty-state"
                />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {models.map((m) => (
                        <ModelCard
                            key={m.id}
                            model={m}
                            usageCount={usageByModelId[m.id] || 0}
                            onEdit={() => setFormModel(m)}
                            onDelete={() => setDeleteTarget(m)}
                        />
                    ))}
                </div>
            )}

            {formModel && (
                <ModelFormDialog
                    model={formModel === "new" ? null : formModel}
                    onClose={() => setFormModel(null)}
                />
            )}

            <DeleteConfirmDialog
                open={!!deleteTarget}
                onOpenChange={(o) => !o && setDeleteTarget(null)}
                itemName={deleteTarget?.name}
                impactCount={deleteTarget ? (usageByModelId[deleteTarget.id] || 0) : 0}
                impactMessage={
                    <>
                        <strong>{deleteTarget ? (usageByModelId[deleteTarget.id] || 0) : 0} ürün</strong> bu modele bağlı. Silersen bu ürünlerin model bilgisi otomatik kaldırılır ve widget&apos;ta model bilgi bloğu gösterilmez.
                    </>
                }
                permanentNote="Model kalıcı olarak silinir. Bu işlem geri alınamaz."
                ackMessage={
                    <>
                        <strong>{deleteTarget ? (usageByModelId[deleteTarget.id] || 0) : 0} ürün</strong> bu silme sonrası model bilgisiz kalacak.
                    </>
                }
                onConfirm={() => {
                    const target = deleteTarget;
                    setDeleteTarget(null);
                    if (!target) return;
                    removeModel(target.id);
                    toast.success(`${target.name} silindi`);
                }}
                testId="model-delete"
            />
        </div>
    );
}

/* ---------- Model card (view-only summary + edit/delete) ---------- */

const ModelCard = ({ model, usageCount, onEdit, onDelete }) => (
    <div className="rounded-xl border border-border bg-surface p-5 flex flex-col hover:border-foreground/25 transition-colors" data-testid={`model-card-${model.id}`}>
        <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 rounded-full bg-foreground text-background grid place-items-center font-display font-bold text-sm shrink-0">
                    {initials(model.name)}
                </div>
                <div className="min-w-0">
                    <div className="font-display text-base font-bold tracking-tight truncate">{model.name}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        {model.height} cm · {model.weight} kg
                    </div>
                </div>
            </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4">
            {[
                { label: "Göğüs", value: model.chest },
                { label: "Bel", value: model.waist },
                { label: "Kalça", value: model.hip },
            ].map((s) => (
                <div key={s.label} className="rounded-lg border border-border bg-muted/30 px-2.5 py-2 text-center">
                    <div className="text-[9px] font-semibold tracking-wider uppercase text-muted-foreground">{s.label}</div>
                    <div className="font-display text-sm font-bold num mt-0.5">{s.value} <span className="text-[9px] font-normal text-muted-foreground">cm</span></div>
                </div>
            ))}
        </div>

        <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t border-border">
            {/* Read-only usage indicator — atama Ürünler sayfasından yapılır. */}
            <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5" data-testid={`model-usage-${model.id}`}>
                <span className="font-mono num rounded bg-muted px-1.5 py-0.5 text-foreground">{usageCount}</span>
                ürüne bağlı
            </div>
            <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit} data-testid={`model-edit-${model.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={onDelete} data-testid={`model-delete-${model.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            </div>
        </div>
    </div>
);

/* ---------- Create / edit dialog ---------- */

const NumField = ({ label, unit, value, onChange, testId }) => (
    <div className="space-y-1.5">
        <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
            {label} <span className="lowercase font-normal text-muted-foreground/60">({unit})</span>
        </span>
        <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 text-sm font-mono num" data-testid={testId} />
    </div>
);

const ModelFormDialog = ({ model, onClose }) => {
    const { addModel, updateModel } = useSizing();
    const isEdit = !!model;
    const [form, setForm] = useState({
        name: model?.name || "",
        gender: model?.gender || "female",
        height: model?.height ?? "",
        weight: model?.weight ?? "",
        chest: model?.chest ?? "",
        waist: model?.waist ?? "",
        hip: model?.hip ?? "",
    });
    const up = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSave = () => {
        if (!form.name.trim()) {
            toast.error("Model adı zorunludur");
            return;
        }
        const payload = {
            name: form.name,
            gender: form.gender,
            height: Number(form.height) || 0,
            weight: Number(form.weight) || 0,
            chest: Number(form.chest) || 0,
            waist: Number(form.waist) || 0,
            hip: Number(form.hip) || 0,
        };
        if (isEdit) {
            updateModel(model.id, payload);
            toast.success(`${payload.name} güncellendi`);
        } else {
            addModel(payload);
            toast.success(`${payload.name} eklendi`);
        }
        onClose();
    };

    return (
        <Sheet open onOpenChange={(o) => !o && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-[440px] p-0 gap-0 flex flex-col overflow-hidden" data-testid="model-form-dialog">
                <div className="px-6 py-5 border-b border-border">
                    <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                        {isEdit ? "Model düzenleniyor" : "Yeni model"}
                    </div>
                    <SheetTitle className="font-display text-lg font-bold tracking-tight mt-0.5">
                        {isEdit ? model.name : "Model bilgilerini gir"}
                    </SheetTitle>
                </div>

                <div className="px-6 py-5 space-y-4 flex-1 overflow-y-auto scroll-thin">
                    <div className="space-y-1.5">
                        <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">Ad Soyad <span className="text-destructive">*</span></span>
                        <Input
                            autoFocus={!isEdit}
                            value={form.name}
                            onChange={(e) => up("name")(e.target.value)}
                            placeholder="örn. Elif Kaya"
                            className="h-9 text-sm"
                            data-testid="model-name-input"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
                            Cinsiyet <span className="lowercase font-normal text-muted-foreground/60">(atandığı ürünün segmentini belirler)</span>
                        </span>
                        <div className="grid grid-cols-2 gap-2" data-testid="model-gender-selector">
                            {[
                                { value: "female", label: "Kadın" },
                                { value: "male",   label: "Erkek" },
                            ].map((opt) => {
                                const active = form.gender === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => up("gender")(opt.value)}
                                        className={`h-9 rounded-md border text-[12.5px] font-medium transition-all ${
                                            active
                                                ? "border-foreground bg-muted/40 text-foreground"
                                                : "border-border bg-background text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                                        }`}
                                        data-testid={`model-gender-${opt.value}`}
                                        aria-pressed={active}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <NumField label="Boy" unit="cm" value={form.height} onChange={up("height")} testId="model-height-input" />
                        <NumField label="Kilo" unit="kg" value={form.weight} onChange={up("weight")} testId="model-weight-input" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <NumField label="Göğüs" unit="cm" value={form.chest} onChange={up("chest")} testId="model-chest-input" />
                        <NumField label="Bel" unit="cm" value={form.waist} onChange={up("waist")} testId="model-waist-input" />
                        <NumField label="Kalça" unit="cm" value={form.hip} onChange={up("hip")} testId="model-hip-input" />
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-border bg-muted/20 flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={onClose}>Vazgeç</Button>
                    <Button size="sm" className="h-9 text-xs gap-1.5 bg-brand hover:bg-brand/90 text-brand-fg" onClick={handleSave} data-testid="model-form-save">
                        <Check className="h-3 w-3" /> {isEdit ? "Kaydet" : "Modeli Ekle"}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
};
